'use client';

import Link from 'next/link';
import Layout from '../../components/Layout';
import SubDocsLayout from '../../components/SubDocsLayout';
import useTranslation from 'next-translate/useTranslation';
import SEOHead from '../../components/SEOHead';
import { useLocalePath } from '../../lib/useLocalePath';
import { useTheme } from '../../lib/ThemeContext';

const TermsOfService = () => {
  const { lang } = useTranslation('common');
  const { prefix } = useLocalePath();
  const { theme } = useTheme();
  const isLt = lang === 'lt';
  const isDarkTheme = theme === 'dark';
  const privacyHref = prefix ? `${prefix}/sub/privacy-policy` : '/sub/privacy-policy';

  const headingClass = isDarkTheme ? 'text-gray-100' : 'text-gray-900';
  const bodyClass = isDarkTheme ? 'text-gray-300' : 'text-gray-700';
  const linkClass = isDarkTheme
    ? 'text-gray-100 underline hover:no-underline'
    : 'text-primary underline hover:no-underline';

  return (
    <Layout>
      <SEOHead
        title={isLt ? 'Naudojimo taisyklės | GadgetFever' : 'Terms of Service | GadgetFever'}
        description={
          isLt
            ? 'GadgetFever dropshipping parduotuvės naudojimo taisyklės, taikomos pirkėjams Europoje.'
            : 'Terms and conditions for using the GadgetFever dropshipping store across Europe.'
        }
        keywords='terms of service, dropshipping, ecommerce, eu consumer rights, returns, gadgetfever'
        canonicalUrl={isLt ? '/lt/sub/naudojimo-taisykles' : '/sub/terms-of-service'}
        lang={lang}
      />

      <SubDocsLayout>
        <h1 className={`text-2xl sm:text-3xl font-semibold mb-4 text-left ${headingClass}`}>
          {isLt ? 'Naudojimo taisyklės' : 'Terms of Service'}
        </h1>
        <p className={`text-base mb-8 text-left leading-relaxed ${bodyClass}`}>
          {isLt
            ? 'Šios taisyklės reglamentuoja pirkimus GadgetFever el. parduotuvėje. Pateikdami užsakymą jūs sutinkate su šiomis sąlygomis, taikomomis pagal ES vartotojų teisių reikalavimus.'
            : 'These terms govern purchases made through GadgetFever. By placing an order, you agree to these conditions, applied in line with EU consumer rights standards.'}
        </p>

        <div className='space-y-8 text-left'>
          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '1. Užsakymai ir kainos' : '1. Orders and pricing'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Užsakymas laikomas priimtu, kai gaunate užsakymo patvirtinimą. Kainos nurodomos eurais ir gali keistis iki užsakymo pateikimo momento.'
                : 'An order is accepted when you receive order confirmation. Prices are shown in EUR and may change until checkout is completed.'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '2. Pristatymas' : '2. Delivery'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Dauguma užsakymų Europoje pristatomi per 7–15 darbo dienų. Galimi vėlavimai dėl logistikos, tiekėjų ar muitinės procesų.'
                : 'Most European orders are delivered within 7–15 business days. Delays may occur due to logistics, supplier processing, or customs procedures.'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '3. Atsisakymo teisė ir grąžinimai' : '3. Withdrawal and returns'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Pagal ES teisę, vartotojai turi 14 dienų atsisakymo teisę nuo prekių gavimo dienos. Grąžinamos prekės turi būti originalios būklės, nebent jos brokuotos.'
                : 'Under EU law, consumers have a 14-day right of withdrawal from the date of delivery. Returned items must be in original condition unless defective.'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '4. Pinigų grąžinimas' : '4. Refunds'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Patvirtinus grąžinimą, pinigai grąžinami per 5–10 darbo dienų į pradinę mokėjimo priemonę.'
                : 'Once a return is approved, refunds are processed within 5–10 business days to the original payment method.'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '5. Garantijos ir neatitikimai' : '5. Warranty and non-conformity'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt
                ? 'Jei prekė neatitinka aprašymo ar yra pažeista, susisiekite su mumis per kontaktų puslapį ir pateikite įrodymus (nuotraukas/video).'
                : 'If goods are not as described or arrive damaged, contact us via the contact page and provide evidence (photos/video).'}
            </p>
          </section>

          <section>
            <h2 className={`text-lg font-semibold mb-2 ${headingClass}`}>{isLt ? '6. Privatumas ir slapukai' : '6. Privacy and cookies'}</h2>
            <p className={`text-sm leading-relaxed ${bodyClass}`}>
              {isLt ? 'Asmens duomenys tvarkomi pagal mūsų ' : 'Personal data is processed according to our '}
              <Link href={privacyHref} className={linkClass}>
                {isLt ? 'Privatumo politiką' : 'Privacy Policy'}
              </Link>
              {isLt ? '.' : '.'}
            </p>
          </section>
        </div>

        <p className={`mt-10 text-sm text-left ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>
          {isLt ? 'Paskutinį kartą atnaujinta:' : 'Last updated:'} {new Date().toLocaleDateString(isLt ? 'lt-LT' : 'en-GB')}
        </p>
      </SubDocsLayout>
    </Layout>
  );
};

export default TermsOfService;
